<template>
  <div class="congratulatory-letter-page" v-loading="loading">
    <div class="toolbar" v-if="!loading && projectInfo && projectInfo.project_code">
      <el-button type="primary" size="medium" :loading="downloading" @click="downloadLetterImage">
        Download the image
      </el-button>
    </div>

    <div v-if="!loading && errorMessage" class="empty-tip">{{ errorMessage }}</div>

    <div v-else-if="!loading" id="congratulatoryLetterPrint" class="letter-print-root">
    <!-- Same mobile fit pattern as public transcript/certificate pages -->
    <ResponsiveCertificateFrame :designWidth="LETTER_WIDTH" :designHeight="letterDesignHeight">
    <div
      ref="certEl"
      class="cert"
      data-certificate-document
      :data-export-width="LETTER_WIDTH"
      :data-export-height="letterDesignHeight">
      <div class="frame"></div>
      <span class="corner tl" v-html="cornerSvg"></span>
      <span class="corner tr" v-html="cornerSvg"></span>
      <span class="corner bl" v-html="cornerSvg"></span>
      <span class="corner br" v-html="cornerSvg"></span>

      <div class="confetti" aria-hidden="true">
        <span class="r" style="left:3.0%;top:9%;--c:#FF7A3D;--rot:28deg;--sc:1.0"></span>
        <span class="c" style="left:5.4%;top:4%;--c:#E8C55A;--rot:0deg;--sc:1.1"></span>
        <span class="s" style="left:2.8%;top:14%;--c:#C9A227;--rot:0deg;--sc:0.9"></span>
        <span class="r" style="left:7.2%;top:20%;--c:#C9A227;--rot:-18deg;--sc:0.85"></span>
        <span class="c" style="left:8.6%;top:9%;--c:#FF5E1A;--rot:0deg;--sc:0.8"></span>
        <span class="r" style="left:95.5%;top:8%;--c:#FF5E1A;--rot:20deg;--sc:1.0"></span>
        <span class="c" style="left:93.2%;top:16%;--c:#E8C55A;--rot:0deg;--sc:1.15"></span>
        <span class="s" style="left:96.6%;top:6%;--c:#FF7A3D;--rot:0deg;--sc:0.85"></span>
        <span class="r" style="left:92.0%;top:22%;--c:#C9A227;--rot:32deg;--sc:0.9"></span>
        <span class="c" style="left:90.2%;top:5%;--c:#FF7A3D;--rot:0deg;--sc:0.75"></span>
        <span class="r" style="left:50%;top:1.5%;--c:#C9A227;--rot:16deg;--sc:0.7"></span>
        <span class="s" style="left:14%;top:60%;--c:#E8C55A;--rot:0deg;--sc:0.7"></span>
        <span class="c" style="left:86%;top:58%;--c:#FF5E1A;--rot:0deg;--sc:0.7"></span>
      </div>

      <div class="inner">
        <header class="head">
          <div class="logos">
            <img class="eae-logo" :src="eaeLogoUrl" alt="EAE — Extracurricular Activity Excellence" crossorigin="anonymous" @load="measureLetterHeight">
            <span class="vline" v-if="projectLogoUrl"></span>
            <img
              v-if="projectLogoUrl"
              class="project-logo"
              :src="projectLogoUrl"
              alt="Competition logo"
              crossorigin="anonymous"
              @load="measureLetterHeight">
          </div>
          <div class="eyebrow">Award Recognition</div>
          <div class="head-rule"></div>
        </header>

        <section class="hero">
          <h1>Celebrating Excellence</h1>
          <div class="ribbon-wrap">
            <span class="ribbon">
              <!-- In-flow SVG caps (not absolute/clip-path) so export keeps both arrows -->
              <span class="ribbon-cap-slot ribbon-cap-l" aria-hidden="true">
                <svg viewBox="0 0 20 40" preserveAspectRatio="none">
                  <polygon points="20,0 20,40 0,20" fill="#FF5E1A"/>
                </svg>
              </span>
              <span class="ribbon-label"><span class="ribbon-label-text">{{ projectName }}</span></span>
              <span class="ribbon-cap-slot ribbon-cap-r" aria-hidden="true">
                <svg viewBox="0 0 20 40" preserveAspectRatio="none">
                  <polygon points="0,0 20,20 0,40" fill="#FF7A3D"/>
                </svg>
              </span>
            </span>
          </div>
        </section>

        <div class="presented">
          <div class="presented-label">Presented to</div>
          <div class="presented-name">{{ schoolName }}</div>
          <div class="chips" v-if="advisorName">
            <span class="chip"><b>Advising Teacher</b> <strong>{{ advisorName }}</strong></span>
          </div>
        </div>

        <p class="copy">
          On behalf of Extracurricular Activity Excellence International, we extend our warmest congratulations to
          <strong>{{ schoolName }}</strong>.
          {{ studentsEnteredLabel }} from {{ schoolName }} entered {{ projectName }} and achieved the following outstanding results, officially recognised:
        </p>

        <div class="awards">
          <div class="awards-head"><span class="star">&#9733;</span> Awards Achieved</div>
          <div v-if="!awards.length" class="award-row">
            <div class="award-name-cell">
              <div class="award-name">
                <span class="award-name-icon" v-html="awardMedalSvg"></span>
                <span class="award-name-text">Recognition</span>
              </div>
            </div>
            <div class="award-body">
              <div class="award-count">Results recorded</div>
              <div class="award-detail">Award details will appear here when published.</div>
            </div>
          </div>
          <div class="award-row" v-for="(award, index) in awards" :key="index">
            <div class="award-name-cell">
              <div class="award-name">
                <span class="award-name-icon" v-html="awardMedalSvg"></span>
                <span class="award-name-text">{{ award.award_name }}</span>
              </div>
            </div>
            <div class="award-body">
              <div class="award-count">{{ award.award_count }}</div>
              <div class="award-detail" v-if="award.award_detail">{{ award.award_detail }}</div>
            </div>
          </div>
        </div>

        <p class="copy">
          Behind every result stands a school that made it possible. We thank
          <strong>{{ schoolName }}</strong>
          for the teaching, encouragement and opportunities it has given these students, and we look forward to seeing them go further.
        </p>

        <div class="signoff">
          <div class="signoff-left">
            <div class="lead">With our congratulations,</div>
            <div class="sig-name">EAE</div>
            <div class="sig-rule"></div>
            <div class="sig-org">Extracurricular Activity Excellence International</div>
          </div>
          <div class="qr-box">
            <div data-certificate-qr class="qr-code">
              <vue-qr
                :text="letterPageUrl"
                :size="200"
                :margin="0"
                :correctLevel="1"
                colorDark="#1A1A1A"
                colorLight="#FFFFFF"
              />
            </div>
            <div class="qr-cap">Scan to verify</div>
          </div>
        </div>
      </div>
    </div>
    </ResponsiveCertificateFrame>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import VueQr from 'vue-qr'
import ResponsiveCertificateFrame from '../../components/ResponsiveCertificateFrame.vue'
import { getAccountApplicationContacts, getCongratulatoryLetterByApplication } from '../../api/eae'
import { DOWNLOAD_REQUIRES_EXTERNAL_BROWSER } from '../../utils/certificateImage'
import { getUserId } from '../../utils/store'

const EAE_LOGO_URL = 'https://ap.learningfirst.io/eae_emial_logo.png'
const LETTER_WIDTH = 860
/* Match eae_congratulation_school.html: padding 11*2 + font 16 * line-height 1.45 ≈ 45.2px */
const RIBBON_HEIGHT = 45
const LETTER_PUBLIC_HOST = 'https://my.eae.international'
const CORNER_SVG = `<svg viewBox="0 0 30 30" width="30" height="30" aria-hidden="true">
  <path d="M15 4 L25 15 L15 26 L5 15 Z" fill="#C9A227"/>
  <path d="M15 9.5 L20 15 L15 20.5 L10 15 Z" fill="#E8C55A"/>
  <circle cx="15" cy="15" r="1.8" fill="#FFFCF2"/>
</svg>`
const AWARD_MEDAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
  <circle cx="12" cy="9" r="6.2" fill="#E8C55A"/>
  <path d="M12 6.4l1 2 2.2.3-1.6 1.6.4 2.2-2-1.1-2 1.1.4-2.2L8.8 8.7l2.2-.3z" fill="#FFFCF2"/>
  <path d="M8.4 14.2L6 22l6-3.4L18 22l-2.4-7.8z" fill="#FF5E1A"/>
</svg>`

export default {
  name: 'CongratulatoryLetter',
  components: { VueQr, ResponsiveCertificateFrame },
  data() {
    const searchId = (() => {
      try {
        const m = (window.location.search || '').match(/(?:^|[?&])id=([^&]*)/)
        return m ? decodeURIComponent(m[1]) : ''
      } catch (e) {
        return ''
      }
    })()
    const searchAdvisor = (() => {
      try {
        const m = (window.location.search || '').match(/(?:^|[?&])advisor_id=([^&]*)/)
        return m ? decodeURIComponent(m[1]) : ''
      } catch (e) {
        return ''
      }
    })()
    return {
      LETTER_WIDTH,
      loading: true,
      downloading: false,
      errorMessage: '',
      id: this.$route.query.id || searchId,
      advisorId: this.$route.query.advisor_id || searchAdvisor || getUserId(),
      projectInfo: {},
      accountInfo: {},
      advisorInfo: {},
      advisorName: '',
      studentsEntered: 0,
      awards: [],
      eaeLogoUrl: EAE_LOGO_URL,
      cornerSvg: CORNER_SVG,
      awardMedalSvg: AWARD_MEDAL_SVG,
      letterDesignHeight: 1200
    }
  },
  computed: {
    projectName() {
      if (!this.projectInfo) return ''
      return this.projectInfo.project_ename || this.projectInfo.project_name || ''
    },
    projectLogoUrl() {
      const raw = (this.projectInfo && this.projectInfo.project_logo) || ''
      if (!raw) return ''
      return String(raw).replace(/ap\.learningfirst\.tech/gi, 'ap.learningfirst.io')
    },
    schoolName() {
      if (!this.accountInfo) return ''
      return this.accountInfo.account_nameEN || this.accountInfo.account_name || ''
    },
    studentsEnteredLabel() {
      const n = Number(this.studentsEntered) || 0
      return n === 1 ? '1 student' : `${n} students`
    },
    letterPageUrl() {
      // Query-only URL: many phone scanners / WeChat drop or mangle `#/...`.
      // index.html + router restore the hash route from eae_page.
      const id = encodeURIComponent(this.id || '')
      const advisor = this.advisorId ? encodeURIComponent(this.advisorId) : ''
      let search = '?eae_page=congratulatoryLetter&id=' + id
      if (advisor) search += '&advisor_id=' + advisor
      return LETTER_PUBLIC_HOST + '/' + search
    }
  },
  mounted() {
    this.loadLetterFonts()
    this.fetchData()
  },
  methods: {
    loadLetterFonts() {
      // Non-blocking: @import of fonts.googleapis.com can stall CSS on some mobile networks.
      if (typeof document === 'undefined') return
      if (document.getElementById('congratulatory-letter-fonts')) return
      const link = document.createElement('link')
      link.id = 'congratulatory-letter-fonts'
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Montserrat:wght@500;600;700&display=swap'
      link.media = 'print'
      link.onload = function () { this.media = 'all' }
      document.head.appendChild(link)
    },
    measureLetterHeight() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.$refs.certEl
          if (!el) return
          const next = Math.max(Math.ceil(el.scrollHeight), 900)
          if (Math.abs(next - this.letterDesignHeight) > 2) {
            this.letterDesignHeight = next
          }
        })
      })
    },
    mongoId(value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (value.$id) return value.$id
      return String(value)
    },
    formatAdvisorName(contact) {
      if (!contact || typeof contact !== 'object') return ''
      if (contact.contact_enName) return String(contact.contact_enName).trim()
      return `${contact.contact_givenName || ''} ${contact.contact_lastName || ''}`.trim()
    },
    applyLetterData(data) {
      this.projectInfo = (data && data.project_info) || {}
      this.accountInfo = (data && data.account_info) || {}
      this.advisorInfo = (data && data.advisor_info) || {}
      this.studentsEntered = (data && data.students_entered) || 0
      this.awards = Array.isArray(data && data.awards) ? data.awards : []
      this.advisorName = this.formatAdvisorName(this.advisorInfo)
    },
    // Public links often omit advisor_id; resolve the application advisor so the chip can render.
    async resolveDefaultAdvisorId() {
      if (this.advisorId) return this.advisorId
      const accountId = this.mongoId(this.accountInfo && this.accountInfo._id)
      const projectId = this.mongoId(this.projectInfo && this.projectInfo._id)
      if (!accountId || !projectId) return ''
      try {
        const res = await getAccountApplicationContacts(accountId, projectId)
        const body = res && res.data ? res.data : {}
        const ids = body.code === 0 && Array.isArray(body.data) ? body.data : []
        return ids[0] ? this.mongoId(ids[0]) : ''
      } catch (e) {
        console.error(e)
        return ''
      }
    },
    async fetchData() {
      if (!this.id) {
        this.loading = false
        this.errorMessage = 'Missing application id.'
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await getCongratulatoryLetterByApplication(this.id, this.advisorId)
        const body = res && res.data ? res.data : {}
        if (body.code !== 0 || !body.data) {
          this.errorMessage = body.msg || 'Unable to load congratulatory letter.'
          return
        }
        this.applyLetterData(body.data)

        if (!this.advisorName) {
          const resolvedId = await this.resolveDefaultAdvisorId()
          if (resolvedId) {
            this.advisorId = resolvedId
            const res2 = await getCongratulatoryLetterByApplication(this.id, resolvedId)
            const body2 = res2 && res2.data ? res2.data : {}
            if (body2.code === 0 && body2.data) {
              this.applyLetterData(body2.data)
            }
          }
        }
      } catch (e) {
        console.error(e)
        this.errorMessage = 'Unable to load congratulatory letter.'
      } finally {
        this.loading = false
        this.measureLetterHeight()
      }
    },
    assertDirectDownloadSupport() {
      const userAgent = navigator.userAgent || ''
      const knownEmbeddedBrowser = /MicroMessenger|Weibo|FBAN|FBAV|Instagram|Line\/|\bwv\b/i.test(userAgent)
      const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
      const knownIOSBrowser = /Safari|CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent)
      if (knownEmbeddedBrowser || (isIOS && !knownIOSBrowser)) {
        const error = new Error('Please open this page in a browser to download the image.')
        error.code = DOWNLOAD_REQUIRES_EXTERNAL_BROWSER
        throw error
      }
    },
    withTimeout(promise, ms) {
      let timer = null
      return Promise.race([
        Promise.resolve(promise).finally(() => {
          if (timer) clearTimeout(timer)
        }),
        new Promise((resolve) => {
          timer = setTimeout(resolve, ms)
        })
      ])
    },
    withTimeoutReject(promise, ms, message) {
      let timer = null
      return new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error(message || 'Operation timed out'))
        }, ms)
        Promise.resolve(promise).then(
          (value) => {
            clearTimeout(timer)
            resolve(value)
          },
          (error) => {
            clearTimeout(timer)
            reject(error)
          }
        )
      })
    },
    waitForImages(element, timeoutMs = 2000) {
      const images = Array.from(element.querySelectorAll('img'))
      return Promise.all(images.map((image) => {
        // Already decoded — never block export.
        if (image.complete && image.naturalWidth > 0) return Promise.resolve()
        return new Promise((resolve) => {
          let settled = false
          const done = () => {
            if (settled) return
            settled = true
            resolve()
          }
          image.addEventListener('load', done, { once: true })
          image.addEventListener('error', done, { once: true })
          // decode() can hang on some data:/blob images — always race a timeout.
          if (image.decode) {
            image.decode().then(done).catch(done)
          }
          setTimeout(done, timeoutMs)
        })
      }))
    },
    cleanupExportStages() {
      Array.from(document.querySelectorAll('body > [data-letter-export-stage="1"]')).forEach((node) => {
        if (node.parentNode) node.parentNode.removeChild(node)
      })
    },
    // Bake loaded logos into data URLs so html2canvas does not drop cross-origin images.
    inlineImagesForExport(source, clone) {
      const srcImgs = source.querySelectorAll('img')
      const cloneImgs = clone.querySelectorAll('img')
      cloneImgs.forEach((img, index) => {
        const src = srcImgs[index]
        if (!src || !src.naturalWidth || !src.naturalHeight) return
        try {
          const canvas = document.createElement('canvas')
          canvas.width = src.naturalWidth
          canvas.height = src.naturalHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(src, 0, 0)
          img.src = canvas.toDataURL('image/png')
        } catch (e) {
          // Cross-origin without CORS — leave URL; html2canvas may still paint it.
        }
      })
    },
    loadImageFromUrl(url) {
      return new Promise((resolve) => {
        const img = new Image()
        let settled = false
        const done = (value) => {
          if (settled) return
          settled = true
          resolve(value)
        }
        img.onload = () => done(img)
        img.onerror = () => done(null)
        setTimeout(() => done(null), 2000)
        img.src = url
      })
    },
    wrapCanvasText(ctx, text, maxWidth) {
      const words = String(text || '').split(/\s+/).filter(Boolean)
      if (!words.length) return ['']
      const lines = []
      let line = ''
      words.forEach((word) => {
        const test = line ? `${line} ${word}` : word
        if (ctx.measureText(test).width > maxWidth && line) {
          lines.push(line)
          line = word
        } else {
          line = test
        }
      })
      if (line) lines.push(line)
      return lines
    },
    // html2canvas mis-renders CSS vertical-align/flex; bake ribbon / chips / award pills as images.
    async bakeAlignedLabelsForExport(clone) {
      await this.bakeRibbonForExport(clone)
      await this.bakeAdvisorChipsForExport(clone)
      await this.bakeAwardNamesForExport(clone)
    },
    async bakeAdvisorChipsForExport(clone) {
      const chips = Array.from(clone.querySelectorAll('.chip'))
      chips.forEach((chip) => {
        const labelEl = chip.querySelector('b')
        const nameEl = chip.querySelector('strong')
        const label = ((labelEl && labelEl.textContent) || 'Advising Teacher').trim()
        const name = ((nameEl && nameEl.textContent) || '').trim()
        if (!name) return

        const padX = 18
        const height = 36
        const gap = 8
        const scale = 2
        const measure = document.createElement('canvas').getContext('2d')
        measure.font = '600 14px Montserrat, Inter, -apple-system, sans-serif'
        const labelW = measure.measureText(label).width
        measure.font = '700 14px Montserrat, Inter, -apple-system, sans-serif'
        const nameW = measure.measureText(name).width
        const width = Math.ceil(padX * 2 + labelW + gap + nameW)

        const canvas = document.createElement('canvas')
        canvas.width = width * scale
        canvas.height = height * scale
        const ctx = canvas.getContext('2d')
        ctx.scale(scale, scale)

        const r = height / 2
        ctx.beginPath()
        ctx.moveTo(r, 0)
        ctx.arcTo(width, 0, width, height, r)
        ctx.arcTo(width, height, 0, height, r)
        ctx.arcTo(0, height, 0, 0, r)
        ctx.arcTo(0, 0, width, 0, r)
        ctx.closePath()
        ctx.fillStyle = '#FFF6EF'
        ctx.fill()
        ctx.strokeStyle = '#F7DCC9'
        ctx.lineWidth = 1
        ctx.stroke()

        const midY = height / 2
        ctx.textBaseline = 'middle'
        ctx.textAlign = 'left'
        ctx.font = '600 14px Montserrat, Inter, -apple-system, sans-serif'
        ctx.fillStyle = '#8A8A8A'
        ctx.fillText(label, padX, midY)
        ctx.font = '700 14px Montserrat, Inter, -apple-system, sans-serif'
        ctx.fillStyle = '#C63F08'
        ctx.fillText(name, padX + labelW + gap, midY)

        const img = document.createElement('img')
        img.src = canvas.toDataURL('image/png')
        img.alt = `${label} ${name}`
        img.width = width
        img.height = height
        Object.assign(img.style, {
          display: 'inline-block',
          width: width + 'px',
          height: height + 'px',
          verticalAlign: 'middle'
        })
        chip.parentNode.replaceChild(img, chip)
      })
    },
    async bakeRibbonForExport(clone) {
      const ribbon = clone.querySelector('.ribbon')
      if (!ribbon) return
      const text = (clone.querySelector('.ribbon-label-text') || {}).textContent || ''
      const width = Math.max(Math.ceil(ribbon.getBoundingClientRect().width), 120)
      const height = RIBBON_HEIGHT
      const tip = 20
      const scale = 2
      const canvas = document.createElement('canvas')
      canvas.width = width * scale
      canvas.height = height * scale
      const ctx = canvas.getContext('2d')
      ctx.scale(scale, scale)
      ctx.clearRect(0, 0, width, height)

      const grad = ctx.createLinearGradient(0, 0, width, height)
      grad.addColorStop(0, '#FF5E1A')
      grad.addColorStop(1, '#FF7A3D')
      ctx.beginPath()
      ctx.moveTo(tip, 0)
      ctx.lineTo(width - tip, 0)
      ctx.lineTo(width, height / 2)
      ctx.lineTo(width - tip, height)
      ctx.lineTo(tip, height)
      ctx.lineTo(0, height / 2)
      ctx.closePath()
      ctx.fillStyle = grad
      ctx.fill()

      ctx.fillStyle = '#FFFFFF'
      ctx.font = '600 16px Montserrat, Inter, -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // textBaseline middle + tiny nudge matches optical center of caps in this banner
      ctx.fillText(text.trim(), width / 2, height / 2 + 0.5)

      const img = document.createElement('img')
      img.src = canvas.toDataURL('image/png')
      img.alt = text.trim()
      img.width = width
      img.height = height
      Object.assign(img.style, {
        display: 'inline-block',
        width: width + 'px',
        height: height + 'px',
        maxWidth: '100%',
        verticalAlign: 'middle',
        boxShadow: '0 4px 14px rgba(255, 94, 26, 0.22)'
      })
      ribbon.parentNode.replaceChild(img, ribbon)
    },
    async bakeAwardNamesForExport(clone) {
      const medalImg = await this.loadImageFromUrl(
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(AWARD_MEDAL_SVG)
      )
      const nodes = Array.from(clone.querySelectorAll('.award-name'))
      for (const el of nodes) {
        const textEl = el.querySelector('.award-name-text')
        const text = (textEl && textEl.textContent) || el.textContent || ''
        const width = Math.max(Math.ceil(el.getBoundingClientRect().width), 80)
        const padX = 10
        const padY = 8
        const icon = 17
        const gap = 5
        const textMax = Math.max(width - padX * 2 - icon - gap, 40)
        const scale = 2
        const measure = document.createElement('canvas').getContext('2d')
        measure.font = '700 14px Montserrat, Inter, -apple-system, sans-serif'
        const lines = this.wrapCanvasText(measure, text.trim(), textMax)
        const lineHeight = 18
        const textBlockH = Math.max(lines.length * lineHeight, icon)
        const height = Math.max(Math.ceil(padY * 2 + textBlockH), 34)

        const canvas = document.createElement('canvas')
        canvas.width = width * scale
        canvas.height = height * scale
        const ctx = canvas.getContext('2d')
        ctx.scale(scale, scale)

        // Pill background
        const r = height / 2
        ctx.beginPath()
        ctx.moveTo(r, 0)
        ctx.arcTo(width, 0, width, height, r)
        ctx.arcTo(width, height, 0, height, r)
        ctx.arcTo(0, height, 0, 0, r)
        ctx.arcTo(0, 0, width, 0, r)
        ctx.closePath()
        ctx.fillStyle = '#FFE9DA'
        ctx.fill()

        // Share one vertical center so icon + label stay optically aligned in export.
        const centerY = height / 2
        const iconY = centerY - icon / 2
        if (medalImg) {
          ctx.drawImage(medalImg, padX, iconY, icon, icon)
        }

        ctx.fillStyle = '#C63F08'
        ctx.font = '700 14px Montserrat, Inter, -apple-system, sans-serif'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'middle'
        const textX = padX + icon + gap
        // Caps sit high with textBaseline middle — nudge down to match medal visual center.
        const textOpticalNudge = 1.5
        const textBlockTop = centerY - textBlockH / 2
        lines.forEach((line, i) => {
          const y = textBlockTop + i * lineHeight + lineHeight / 2 + textOpticalNudge
          ctx.fillText(line, textX, y)
        })

        const img = document.createElement('img')
        img.src = canvas.toDataURL('image/png')
        img.alt = text.trim()
        img.width = width
        img.height = height
        Object.assign(img.style, {
          display: 'block',
          width: '100%',
          height: height + 'px',
          maxWidth: '100%'
        })
        el.parentNode.replaceChild(img, el)
      }
    },
    sanitizeFileName(value) {
      return String(value || 'certificate')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '_')
    },
    canvasToBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error('Unable to create image'))
        }, 'image/png')
      })
    },
    prepareExportClone(source, width) {
      const clone = source.cloneNode(true)
      clone.classList.add('is-exporting')
      Object.assign(clone.style, {
        width: width + 'px',
        maxWidth: width + 'px',
        margin: '0',
        boxShadow: 'none',
        transform: 'none',
        position: 'relative'
      })
      // Keep theme vars on the export node itself (clone is detached from page root).
      const vars = {
        '--orange': '#FF5E1A',
        '--orange-hover': '#FF7A3D',
        '--orange-deep': '#C63F08',
        '--tint-soft': '#FFF6EF',
        '--tint-strong': '#FFE9DA',
        '--gold': '#C9A227',
        '--gold-light': '#E8C55A',
        '--gold-pale': '#FFFCF2',
        '--gold-ink': '#A9821A',
        '--ink': '#1A1A1A',
        '--body-text': '#3C3A36',
        '--muted': '#8A8A8A',
        '--hairline': '#F0ECE8',
        '--serif-display': "'Cinzel', 'Georgia', serif",
        '--serif-body': "'Cormorant Garamond', 'Georgia', serif",
        '--sans-label': "'Montserrat', 'Inter', -apple-system, sans-serif"
      }
      Object.keys(vars).forEach((key) => clone.style.setProperty(key, vars[key]))

      const inner = clone.querySelector('.inner')
      if (inner) {
        // Keep desktop padding — html2canvas windowWidth can otherwise hit the mobile media query.
        inner.style.padding = '52px 58px 46px'
        inner.style.boxSizing = 'border-box'
        inner.style.position = 'relative'
        inner.style.zIndex = '2'
      }
      const frame = clone.querySelector('.frame')
      if (frame) {
        frame.style.top = '18px'
        frame.style.right = '18px'
        frame.style.bottom = '18px'
        frame.style.left = '18px'
      }
      const signoff = clone.querySelector('.signoff')
      if (signoff) {
        signoff.style.display = 'flex'
        signoff.style.flexDirection = 'row'
        signoff.style.alignItems = 'flex-end'
        signoff.style.justifyContent = 'space-between'
        signoff.style.gap = '26px'
      }
      const qrBox = clone.querySelector('.qr-box')
      if (qrBox) {
        qrBox.style.flex = '0 0 auto'
        qrBox.style.marginLeft = 'auto'
        qrBox.style.textAlign = 'center'
      }
      const title = clone.querySelector('h1')
      if (title) {
        title.style.background = 'none'
        title.style.webkitBackgroundClip = 'border-box'
        title.style.backgroundClip = 'border-box'
        title.style.webkitTextFillColor = '#A9821A'
        title.style.color = '#A9821A'
        title.style.fontSize = '46px'
        title.style.textAlign = 'center'
        title.style.width = '100%'
      }
      ;['.hero', '.presented', '.ribbon-wrap', '.presented-label', '.presented-name'].forEach((sel) => {
        clone.querySelectorAll(sel).forEach((el) => {
          el.style.textAlign = 'center'
          el.style.width = '100%'
        })
      })
      const ribbon = clone.querySelector('.ribbon')
      if (ribbon) {
        ribbon.style.display = 'inline-flex'
        ribbon.style.alignItems = 'stretch'
        ribbon.style.height = RIBBON_HEIGHT + 'px'
        ribbon.style.maxWidth = '100%'
        ribbon.style.verticalAlign = 'middle'
        ribbon.style.clipPath = 'none'
        ribbon.style.background = 'transparent'
        ribbon.style.padding = '0'
        ribbon.style.boxShadow = 'none'
        ribbon.style.filter = 'none'
        ribbon.style.boxSizing = 'border-box'
        ribbon.style.overflow = 'visible'
      }
      const ribbonLabel = clone.querySelector('.ribbon-label')
      if (ribbonLabel) {
        // table-cell + vertical-align is reliable in html2canvas (flex/line-height often drift)
        ribbonLabel.style.display = 'table'
        ribbonLabel.style.height = RIBBON_HEIGHT + 'px'
        ribbonLabel.style.padding = '0'
        ribbonLabel.style.boxSizing = 'border-box'
        ribbonLabel.style.boxShadow = '0 4px 14px rgba(255, 94, 26, 0.22)'
        ribbonLabel.style.overflow = 'hidden'
      }
      const ribbonLabelText = clone.querySelector('.ribbon-label-text')
      if (ribbonLabelText) {
        ribbonLabelText.style.display = 'table-cell'
        ribbonLabelText.style.verticalAlign = 'middle'
        ribbonLabelText.style.textAlign = 'center'
        ribbonLabelText.style.height = RIBBON_HEIGHT + 'px'
        ribbonLabelText.style.padding = '0 24px'
        ribbonLabelText.style.lineHeight = '1.2'
        ribbonLabelText.style.whiteSpace = 'nowrap'
        // Caps sit high in the em-box; nudge down for optical center in export
        ribbonLabelText.style.position = 'relative'
        ribbonLabelText.style.top = '1px'
      }
      clone.querySelectorAll('.ribbon-cap-slot').forEach((slot) => {
        slot.style.position = 'relative'
        slot.style.flex = '0 0 20px'
        slot.style.width = '20px'
        slot.style.height = RIBBON_HEIGHT + 'px'
        slot.style.alignSelf = 'stretch'
        slot.style.display = 'block'
        slot.style.overflow = 'visible'
      })
      clone.querySelectorAll('.ribbon-cap-slot svg').forEach((svg) => {
        svg.style.position = 'absolute'
        svg.style.left = '0'
        svg.style.top = '0'
        svg.style.width = '100%'
        svg.style.height = RIBBON_HEIGHT + 'px'
        svg.setAttribute('height', String(RIBBON_HEIGHT))
        svg.style.display = 'block'
      })
      const chips = clone.querySelector('.chips')
      if (chips) {
        chips.style.display = 'flex'
        chips.style.justifyContent = 'center'
        chips.style.alignItems = 'center'
        chips.style.width = '100%'
      }
      clone.querySelectorAll('.chip').forEach((chip) => {
        chip.style.display = 'inline-flex'
        chip.style.alignItems = 'center'
        chip.style.justifyContent = 'center'
        chip.style.gap = '8px'
        chip.style.lineHeight = '1'
        chip.style.padding = '8px 18px'
        chip.style.verticalAlign = 'middle'
        chip.style.textAlign = 'center'
        chip.style.boxSizing = 'border-box'
      })
      const awardsHead = clone.querySelector('.awards-head')
      if (awardsHead) {
        awardsHead.style.display = 'flex'
        awardsHead.style.alignItems = 'center'
        awardsHead.style.justifyContent = 'flex-start'
      }
      clone.querySelectorAll('.award-row').forEach((row) => {
        row.style.display = 'flex'
        row.style.alignItems = 'center'
        row.style.width = '100%'
        row.style.gap = '18px'
        row.style.padding = '15px 22px'
        row.style.boxSizing = 'border-box'
      })
      clone.querySelectorAll('.award-name-cell').forEach((el) => {
        el.style.display = 'block'
        el.style.flex = '1.8 1 0'
        el.style.minWidth = '0'
        el.style.width = 'auto'
        el.style.maxWidth = 'none'
        el.style.paddingRight = '0'
        el.style.overflow = 'visible'
        el.style.textAlign = 'left'
      })
      clone.querySelectorAll('.award-name').forEach((el) => {
        el.style.display = 'table'
        el.style.width = '100%'
        el.style.maxWidth = '100%'
        el.style.borderCollapse = 'separate'
        el.style.padding = '8px 12px 8px 10px'
        el.style.boxSizing = 'border-box'
        el.style.backgroundImage = 'none'
        el.style.lineHeight = '1.25'
      })
      clone.querySelectorAll('.award-name-icon').forEach((el) => {
        el.style.display = 'table-cell'
        el.style.verticalAlign = 'middle'
        el.style.width = '17px'
        el.style.height = '17px'
        el.style.paddingRight = '5px'
        el.style.lineHeight = '0'
      })
      clone.querySelectorAll('.award-name-icon svg').forEach((svg) => {
        svg.style.display = 'block'
        svg.style.width = '17px'
        svg.style.height = '17px'
        svg.style.verticalAlign = 'middle'
      })
      clone.querySelectorAll('.award-name-text').forEach((el) => {
        el.style.display = 'table-cell'
        el.style.verticalAlign = 'middle'
        el.style.textAlign = 'left'
        el.style.whiteSpace = 'normal'
        el.style.wordBreak = 'break-word'
        el.style.lineHeight = '1.25'
      })
      clone.querySelectorAll('.award-body').forEach((el) => {
        el.style.display = 'block'
        el.style.flex = '1 1 0'
        el.style.minWidth = '0'
        el.style.width = 'auto'
        el.style.verticalAlign = 'middle'
        el.style.textAlign = 'left'
      })
      clone.querySelectorAll('.award-count').forEach((el) => {
        el.style.textAlign = 'left'
        el.style.lineHeight = '1.35'
        el.style.whiteSpace = 'normal'
        el.style.wordBreak = 'break-word'
        el.style.position = 'relative'
        el.style.zIndex = '1'
      })
      clone.querySelectorAll('.award-detail').forEach((el) => {
        el.style.textAlign = 'left'
        el.style.lineHeight = '1.4'
        el.style.whiteSpace = 'normal'
        el.style.wordBreak = 'break-word'
      })
      // clip-path confetti can stall html2canvas — drop it from the export clone.
      clone.querySelectorAll('.confetti').forEach((el) => el.remove())
      // Avoid capturing any stray vue-qr helper canvases outside the QR box.
      clone.querySelectorAll('canvas').forEach((canvasEl) => {
        if (!canvasEl.closest('[data-certificate-qr]')) {
          canvasEl.remove()
        }
      })
      return clone
    },
    async downloadLetterImage() {
      if (this.downloading) return
      this.downloading = true
      this.cleanupExportStages()
      const cert = this.$el.querySelector('[data-certificate-document]')
      if (!cert) {
        this.downloading = false
        this.$message.error('Letter not found')
        return
      }

      const width = LETTER_WIDTH
      const stage = document.createElement('div')
      stage.setAttribute('data-letter-export-stage', '1')
      // Keep stage in normal layout flow off-screen; huge negative left can stall html2canvas.
      Object.assign(stage.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        width: width + 'px',
        opacity: '0',
        pointerEvents: 'none',
        zIndex: '-1',
        overflow: 'visible',
        background: '#ffffff'
      })
      const clone = this.prepareExportClone(cert, width)
      this.inlineImagesForExport(cert, clone)
      stage.appendChild(clone)
      document.body.appendChild(stage)

      try {
        this.assertDirectDownloadSupport()
        // Google Fonts @import can leave fonts.ready pending for a long time — don't block download.
        if (document.fonts && document.fonts.ready) {
          await this.withTimeout(document.fonts.ready, 1200)
        }
        await this.waitForImages(clone, 1500)
        await new Promise((resolve) => requestAnimationFrame(resolve))

        // Bake ribbon / chips / award pills to canvas so export centering is pixel-accurate.
        await this.withTimeoutReject(
          this.bakeAlignedLabelsForExport(clone),
          8000,
          'Label bake timed out'
        )
        await this.waitForImages(clone, 300)
        await new Promise((resolve) => requestAnimationFrame(resolve))

        // Use content height only — inflated designHeight leaves empty bands that look off-center.
        const height = Math.max(
          Math.ceil(clone.scrollHeight),
          Math.ceil(clone.getBoundingClientRect().height),
          900
        )
        stage.style.height = height + 'px'
        clone.style.height = 'auto'
        clone.style.minHeight = height + 'px'

        // Desktop windowWidth keeps export layout stable (no mobile reflow).
        const captureStarted = performance.now()
        const canvas = await this.withTimeoutReject(
          html2canvas(clone, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
            // Default imageTimeout is 15000ms per image and commonly causes a multi-second stall.
            imageTimeout: 1500,
            width,
            height,
            windowWidth: 1280,
            windowHeight: Math.max(height + 80, 900),
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
            foreignObjectRendering: false
          }),
          15000,
          'Image capture timed out'
        )
        if (process.env.NODE_ENV !== 'production') {
          console.info('[congratulatoryLetter] html2canvas ms', Math.round(performance.now() - captureStarted))
        }

        const blob = await this.withTimeoutReject(this.canvasToBlob(canvas), 8000, 'Image encode timed out')
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const fileName = this.sanitizeFileName([
          'Congratulatory_Letter',
          this.projectInfo.project_code || '',
          this.schoolName || 'School'
        ].filter(Boolean).join('_'))
        link.href = url
        link.download = fileName + '.png'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        this.$message.success('Image downloaded')
      } catch (error) {
        console.error('Unable to download congratulatory letter image', error)
        if (error && error.code === DOWNLOAD_REQUIRES_EXTERNAL_BROWSER) {
          this.$alert(
            'Please open this page in Safari, Chrome, Edge, or another browser to download the image.',
            'Open in a browser',
            {
              confirmButtonText: 'OK',
              type: 'info'
            }
          ).catch(() => {})
        } else {
          this.$message.error('Unable to download the image. Please try again.')
        }
      } finally {
        if (stage.parentNode) {
          stage.parentNode.removeChild(stage)
        }
        this.cleanupExportStages()
        this.downloading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.congratulatory-letter-page {
  min-height: 100vh;
  background: radial-gradient(120% 120% at 50% 0%, #F3EEE3 0%, #E7E0D2 100%);
  font-family: 'Montserrat', 'Inter', -apple-system, sans-serif;
  color: #1A1A1A;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px 32px;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.letter-print-root {
  width: 100%;
  max-width: 860px;
}

.toolbar {
  width: 100%;
  max-width: 860px;
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-end;
}

.empty-tip {
  margin-top: 40px;
  color: #666;
  font-size: 15px;
}

.cert {
  --orange: #FF5E1A;
  --orange-hover: #FF7A3D;
  --orange-deep: #C63F08;
  --tint-soft: #FFF6EF;
  --tint-strong: #FFE9DA;
  --gold: #C9A227;
  --gold-light: #E8C55A;
  --gold-pale: #FFFCF2;
  --gold-ink: #A9821A;
  --ink: #1A1A1A;
  --body-text: #3C3A36;
  --muted: #8A8A8A;
  --hairline: #F0ECE8;
  --serif-display: 'Cinzel', 'Georgia', serif;
  --serif-body: 'Cormorant Garamond', 'Georgia', serif;
  --sans-label: 'Montserrat', 'Inter', -apple-system, sans-serif;

  /* Fixed design size; ResponsiveCertificateFrame scales it on phone like transcripts. */
  width: 860px;
  max-width: 860px;
  position: relative;
  background: #FFFFFF;
  box-shadow: 0 18px 50px rgba(60, 45, 20, 0.16);
  font-family: var(--sans-label);
  color: var(--ink);
  box-sizing: border-box;
}

.cert.is-exporting {
  max-width: 860px !important;
  width: 860px !important;
  box-shadow: none;
}

.cert.is-exporting .confetti span {
  animation: none !important;
}

/* Lock desktop layout while exporting (html2canvas can emulate a narrow window). */
.cert.is-exporting .inner {
  padding: 52px 58px 46px !important;
}
.cert.is-exporting h1 {
  font-size: 46px !important;
}
.cert.is-exporting .presented-name {
  font-size: 40px !important;
}
.cert.is-exporting .hero,
.cert.is-exporting .presented,
.cert.is-exporting .presented-label,
.cert.is-exporting .presented-name,
.cert.is-exporting .ribbon-wrap {
  text-align: center !important;
  width: 100%;
}
.cert.is-exporting .chips {
  display: flex !important;
  justify-content: center !important;
  width: 100% !important;
}
.cert.is-exporting .ribbon {
  display: inline-flex !important;
  align-items: stretch !important;
  height: 45px !important;
  vertical-align: middle !important;
  background: transparent !important;
  padding: 0 !important;
  clip-path: none !important;
  box-shadow: none !important;
  filter: none !important;
  overflow: visible !important;
}
.cert.is-exporting .ribbon-label {
  display: table !important;
  height: 45px !important;
  padding: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  box-shadow: 0 4px 14px rgba(255, 94, 26, 0.22);
}
.cert.is-exporting .ribbon-label-text {
  display: table-cell !important;
  vertical-align: middle !important;
  text-align: center !important;
  height: 45px !important;
  padding: 0 24px !important;
  line-height: 1.2 !important;
  white-space: nowrap !important;
  position: relative !important;
  top: 1px !important;
}
.cert.is-exporting .ribbon-cap-slot {
  position: relative !important;
  flex: 0 0 20px !important;
  width: 20px !important;
  height: 45px !important;
  align-self: stretch !important;
  display: block !important;
  overflow: visible !important;
}
.cert.is-exporting .ribbon-cap-slot svg {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  width: 100% !important;
  height: 45px !important;
  display: block !important;
}
.cert.is-exporting .chip {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  text-align: center !important;
  vertical-align: middle !important;
  line-height: 1 !important;
  padding: 8px 18px !important;
  box-sizing: border-box !important;
}
.cert.is-exporting .award-row {
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  gap: 18px !important;
}
.cert.is-exporting .award-name-cell {
  display: block !important;
  flex: 1.8 1 0 !important;
  min-width: 0 !important;
  width: auto !important;
  max-width: none !important;
  padding-right: 0 !important;
  overflow: visible !important;
  text-align: left !important;
}
.cert.is-exporting .award-name {
  display: table !important;
  width: 100% !important;
  max-width: 100% !important;
  border-collapse: separate !important;
  padding: 8px 12px 8px 10px !important;
  background-image: none !important;
  line-height: 1.25 !important;
}
.cert.is-exporting .award-name-icon {
  display: table-cell !important;
  vertical-align: middle !important;
  width: 17px !important;
  height: 17px !important;
  padding-right: 5px !important;
  line-height: 0 !important;
}
.cert.is-exporting .award-name-icon svg {
  display: block !important;
  width: 17px !important;
  height: 17px !important;
  vertical-align: middle !important;
}
.cert.is-exporting .award-name-text {
  display: table-cell !important;
  vertical-align: middle !important;
  text-align: left !important;
  white-space: normal !important;
  word-break: break-word !important;
  line-height: 1.25 !important;
}
.cert.is-exporting .award-body {
  display: block !important;
  flex: 1 1 0 !important;
  min-width: 0 !important;
  width: auto !important;
  text-align: left !important;
}
.cert.is-exporting .award-count,
.cert.is-exporting .award-detail {
  text-align: left !important;
  white-space: normal !important;
  word-break: break-word !important;
}
.cert.is-exporting .signoff {
  flex-direction: row !important;
  align-items: flex-end !important;
  justify-content: space-between !important;
}
.cert.is-exporting .qr-box {
  margin-left: auto !important;
  flex: 0 0 auto !important;
}

.frame {
  position: absolute;
  top: 18px;
  right: 18px;
  bottom: 18px;
  left: 18px;
  border: 2px solid var(--gold);
  z-index: 2;
  pointer-events: none;
}
.frame::before {
  content: "";
  position: absolute;
  top: 6px;
  right: 6px;
  bottom: 6px;
  left: 6px;
  border: 1px solid var(--gold-light);
}
.corner {
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 3;
  pointer-events: none;
}
.corner.tl { top: 4px; left: 4px; }
.corner.tr { top: 4px; right: 4px; transform: scaleX(-1); }
.corner.bl { bottom: 4px; left: 4px; transform: scaleY(-1); }
.corner.br { bottom: 4px; right: 4px; transform: scale(-1, -1); }

.confetti {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}
.confetti span {
  position: absolute;
  display: block;
  background: var(--c);
  transform: rotate(var(--rot)) scale(var(--sc));
  animation: floaty 7s ease-in-out infinite;
}
.confetti .r { width: 7px; height: 15px; border-radius: 2px; opacity: 0.8; }
.confetti .c { width: 9px; height: 9px; border-radius: 50%; opacity: 0.75; }
.confetti .s {
  width: 14px;
  height: 14px;
  opacity: 0.85;
  clip-path: polygon(50% 0, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
}
.confetti span:nth-child(2n) { animation-duration: 8.4s; animation-delay: -1.6s; }
.confetti span:nth-child(3n) { animation-duration: 9.6s; animation-delay: -3.3s; }

@keyframes floaty {
  0%, 100% { transform: translateY(0) rotate(var(--rot)) scale(var(--sc)); }
  50% { transform: translateY(-6px) rotate(calc(var(--rot) + 12deg)) scale(var(--sc)); }
}

.inner {
  position: relative;
  z-index: 2;
  padding: 52px 58px 46px;
}

.head { text-align: center; position: relative; }
.logos {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}
.logos .vline {
  width: 1px;
  height: 52px;
  background: linear-gradient(180deg, transparent, var(--hairline) 20%, var(--hairline) 80%, transparent);
}
.logos img.eae-logo {
  width: 196px;
  height: auto;
  max-height: 72px;
  object-fit: contain;
  display: block;
}
.logos img.project-logo {
  height: 58px;
  width: auto;
  max-width: 290px;
  object-fit: contain;
  display: block;
}
.eyebrow {
  margin-top: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 4.5px;
  text-transform: uppercase;
  color: var(--orange);
}
.head-rule {
  width: 64px;
  height: 2px;
  background: var(--gold);
  margin: 14px auto 0;
}

.hero { text-align: center; padding: 22px 0 6px; }
h1 {
  font-family: var(--serif-display);
  font-weight: 700;
  font-size: 46px;
  letter-spacing: 0.06em;
  line-height: 1.05;
  text-transform: uppercase;
  margin-top: 6px;
  color: var(--gold-ink);
  // Solid gold keeps screen/export consistent; gradient text breaks in html2canvas.
  background: none;
  -webkit-text-fill-color: currentColor;
}
.ribbon-wrap {
  margin-top: 18px;
  width: 100%;
  text-align: center;
}
/* Fixed-height ribbon matched to design HTML (~45.2px → 45px); text centered. */
.ribbon {
  display: inline-flex;
  align-items: stretch;
  height: 45px;
  max-width: 100%;
  vertical-align: middle;
  box-sizing: border-box;
  overflow: visible;
  filter: drop-shadow(0 4px 14px rgba(255, 94, 26, 0.22));
}
.ribbon-cap-slot {
  position: relative;
  flex: 0 0 20px;
  width: 20px;
  height: 45px;
  align-self: stretch;
  display: block;
  overflow: visible;
  pointer-events: none;
}
.ribbon-cap-slot svg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 45px;
  display: block;
}
.ribbon-label {
  display: table;
  flex: 0 1 auto;
  min-width: 0;
  height: 45px;
  background: linear-gradient(135deg, var(--orange) 0%, var(--orange-hover) 100%);
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.08em;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.ribbon-label-text {
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  height: 45px;
  padding: 0 24px;
  line-height: 1.2;
  white-space: nowrap;
  /* Optical nudge: caps sit high in the em-box / html2canvas export */
  position: relative;
  top: 1px;
}

.presented {
  text-align: center;
  margin-top: 30px;
  width: 100%;
}
.presented-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 3.4px;
  text-transform: uppercase;
  color: var(--muted);
  text-align: center;
}
.presented-name {
  display: block;
  width: 100%;
  font-family: var(--serif-body);
  font-weight: 600;
  font-size: 40px;
  line-height: 1.2;
  margin-top: 10px;
  color: var(--ink);
  text-align: center;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  width: 100%;
}
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--tint-soft);
  border: 1px solid #F7DCC9;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 14px;
  line-height: 1;
  color: var(--ink);
  text-align: center;
  vertical-align: middle;
  box-sizing: border-box;
}
.chip b {
  color: var(--muted);
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
}
.chip strong {
  color: var(--orange-deep);
  font-weight: 700;
  line-height: 1;
}

p.copy {
  font-family: var(--serif-body);
  font-size: 18.5px;
  line-height: 1.7;
  color: var(--body-text);
  margin-top: 26px;
  font-weight: 500;
}

.awards { margin-top: 28px; width: 100%; }
.awards-head {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--orange);
  padding-bottom: 11px;
  border-bottom: 1px solid var(--hairline);
  margin-bottom: 14px;
}
.awards-head .star { color: var(--gold); font-size: 14px; }
/* Proportional split: award_name ~1.8, award_count ~1; name pill fills column width. */
.award-row {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
  background: linear-gradient(90deg, var(--tint-soft) 0%, #FFFAF6 100%);
  border-left: 4px solid var(--gold-light);
  border-radius: 12px;
  padding: 15px 22px;
  box-sizing: border-box;
}
.award-row + .award-row { margin-top: 10px; }
.award-name-cell {
  flex: 1.8 1 0;
  min-width: 0;
  width: auto;
  text-align: left;
}
.award-name {
  display: table;
  width: 100%;
  max-width: 100%;
  border-collapse: separate;
  background-color: var(--tint-strong);
  color: var(--orange-deep);
  font-weight: 700;
  font-size: 14px;
  line-height: 1.25;
  padding: 8px 12px 8px 10px;
  border-radius: 999px;
  box-sizing: border-box;
}
.award-name-icon {
  display: table-cell;
  vertical-align: middle;
  width: 17px;
  height: 17px;
  padding-right: 5px;
  line-height: 0;
}
.award-name-icon ::v-deep svg {
  display: block;
  width: 17px;
  height: 17px;
  vertical-align: middle;
}
.award-name-text {
  display: table-cell;
  vertical-align: middle;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.25;
}
.award-body {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  text-align: left;
}
.award-count {
  position: relative;
  z-index: 1;
  font-size: 19px;
  font-weight: 700;
  color: var(--gold-ink);
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}
.award-detail {
  font-size: 13px;
  line-height: 1.4;
  color: var(--muted);
  margin-top: 2px;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.signoff {
  margin-top: 40px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 26px;
}
.signoff-left .lead {
  font-family: var(--serif-body);
  font-size: 18px;
  color: var(--body-text);
  font-weight: 500;
}
.sig-name {
  font-family: var(--serif-display);
  font-size: 23px;
  font-weight: 600;
  letter-spacing: 2px;
  margin-top: 12px;
  color: var(--ink);
}
.sig-rule {
  width: 180px;
  height: 2px;
  background: var(--gold);
  margin-top: 12px;
}
.sig-org {
  font-size: 11px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--muted);
  margin-top: 8px;
}
.qr-box { text-align: center; }
.qr-code {
  display: inline-block;
  background: #fff;
  padding: 8px;
  border: 1px solid #E3D9BE;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(60, 45, 20, 0.10);
}
.qr-box ::v-deep img {
  width: 116px;
  height: 116px;
  display: block;
}
.qr-cap {
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--muted);
  margin-top: 8px;
}

@media (prefers-reduced-motion: reduce) {
  .confetti span { animation: none !important; }
}
@media print {
  .congratulatory-letter-page {
    background: #FFFFFF;
    padding: 0;
  }
  .toolbar { display: none !important; }
  .cert { box-shadow: none; width: 100%; }
  .confetti span { animation: none !important; }
}
@media (max-width: 767px) {
  .congratulatory-letter-page {
    padding: 10px 8px 24px;
  }
  .toolbar {
    ::v-deep .el-button {
      width: 100%;
      min-height: 44px;
    }
  }
}
</style>
